import { AssistantContext } from './types';
import { IconName } from '@ardoq/icons';
import { t, type I18nLocale } from '@ardoq/i18n';

/** Returns the display name for the source badge in the assistant footer. */
export const getDisplayName = (
  context: AssistantContext,
  i18nLocale: I18nLocale
): string => {
  switch (context.assetType) {
    case 'report':
      return context.name;
    case 'dashboard':
      return context.name;
    case 'viewpoint_browse':
      return 'Viewpoints';
    case 'viewpoint_active':
      return context.name;
    case 'metamodel':
      return context.name;
    case 'initiative':
      return context.name;
    case 'future_state':
      return context.name;
    case 'component':
      return context.name;
    case 'top_level':
      return t(i18nLocale, 'assistant:pageName.allSources', 'All sources');
    default: {
      const _exhaustiveCheck: never = context;
      return _exhaustiveCheck;
    }
  }
};

/** Returns the icon shown next to the source badge in the assistant footer, or undefined when no icon should render. */
export const getSourceIcon = (
  context: AssistantContext
): IconName | undefined => {
  switch (context.assetType) {
    case 'report':
      return IconName.DESCRIPTION_OUTLINED;
    case 'dashboard':
      return IconName.PIE_CHART_FILLED;
    case 'viewpoint_browse':
    case 'viewpoint_active':
      return IconName.VIEWPOINT;
    case 'metamodel':
      return IconName.METAMODEL;
    case 'initiative':
      return IconName.INITIATIVE;
    case 'future_state':
      return IconName.TRANSFORMATION_FUTURE_STATE;
    case 'component':
      return IconName.COMPONENT;
    case 'top_level':
      return undefined;
    default: {
      const _exhaustiveCheck: never = context;
      return _exhaustiveCheck;
    }
  }
};

/** Returns the tooltip text shown on the source tag in the assistant footer. */
export const getContextTooltip = (
  context: AssistantContext,
  i18nLocale: I18nLocale
): string => {
  if (context.assetType === 'top_level') {
    return t(
      i18nLocale,
      'assistant:tooltips.dataScopeAll',
      'AI has access to all your Ardoq data.'
    );
  }
  return t(
    i18nLocale,
    'assistant:tooltips.dataScope',
    'AI focuses here, but can access all Ardoq data.'
  );
};
